"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, TrendingUp, Snowflake, Sun, Building2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityPatterns = [{
  id: "peak-winter",
  season: "Oct – Mar",
  label: "Peak Transit & Wedding Surge",
  icon: Snowflake,
  description: "High tourist transit to Himachal coupled with massive regional banquet and wedding dates. Focus on premium weekend rate fencing and strict minimum length-of-stay.",
  strategy: "High ADR capture & non-refundable weekend tiers"
}, {
  id: "summer-transit",
  season: "Apr – Jun",
  label: "Hill Gateway & Corporate Flow",
  icon: Sun,
  description: "Heavy northward family transit travelers alongside steady IT Park and Mohali corporate traffic. Real-time intraday pricing maximizes last-minute highway captures.",
  strategy: "Dynamic stop-sell controls & direct highway packages"
}, {
  id: "shoulder-monsoon",
  season: "Jul – Sep",
  label: "Corporate & MICE Base",
  icon: Building2,
  description: "Domestic corporate travel, institutional conferences, and localized staycations drive base occupancy. Yield strategies prioritize corporate volume protection without room dumping.",
  strategy: "B2B negotiated yields & OTA targeted geo-promotions"
}];
const challenges = [{
  id: 1,
  title: "Mid-Week vs. Weekend Polarization",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Balancing high-volume IT Park / government corporate demand during Tuesday–Thursday with leisure transit and wedding groups over weekends. We calibrate dynamic fences to protect average room rates across both segments.",
  metricLabel: "Strategy focus",
  metricValue: "Dual-segment yielding & day-of-week rate fencing"
}, {
  id: 2,
  title: "Comp-Set Price Wars in Zirakpur & Mohali",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Manage intense inventory competition along the airport and highway corridors. Automated competitive rate tracking and demand pacing ensure your property captures high-yield bookings without knee-jerk discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "OTA Margin Squeeze & Channel Disparity",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Eliminate unauthorized wholesaler discounting across meta-search channels and minimize commission leakages. We enforce strict parity and deploy high-converting direct booking incentives for regional business travelers.",
  metricLabel: "Strategy focus",
  metricValue: "Strict rate parity & direct corporate booking loyalty"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapechandigarh" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Chandigarh
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Chandigarh operates as the primary commercial hub and transit gateway connecting Punjab, Haryana, and Himachal Pradesh. From Rajiv Gandhi IT Park business accounts to high-value weekend transit leisure, Sunil Singh builds precision revenue frameworks tailored to Tri-city properties.
          </p>
        </div>

        {/* Seasonality & Demand Cycles Block */}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 pb-4 border-b border-border">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary flex items-center gap-1.5">
                <TrendingUp className="size-3.5" /> Tri-City Seasonality & Demand Rhythm
              </span>
              <h3 className="font-serif text-2xl font-bold text-foreground mt-1">
                Annual Revenue Pacing & Market Cycles
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2 sm:mt-0 max-w-md">
              Structured forecasting aligned with corporate business travel and Himachal transit patterns.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityPatterns.map((item, index) => {
            const IconComponent = item.icon;
            return <div key={item.id} data-index={index} className="rounded-lg border border-border bg-card p-6 flex flex-col justify-between hover:border-secondary/50 transition-[border-color,box-shadow] duration-300">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold px-2.5 py-1 rounded-md bg-secondary/15 text-secondary-foreground">
                        <IconComponent className="size-3.5 text-secondary" />
                        {item.season}
                      </span>
                      <span className="text-xs font-mono text-muted-foreground">Cycle 0{index + 1}</span>
                    </div>
                    <h4 className="font-semibold text-lg text-foreground mb-2">
                      {item.label}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  <div className="mt-5 pt-4 border-t border-border/70 text-xs">
                    <span className="font-medium text-foreground block mb-0.5">Focus:</span>
                    <span className="text-muted-foreground">{item.strategy}</span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* 3-Column Challenge Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Chandigarh hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}